/**
 * The board's props.
 *
 * `boardPropsAtPly` is the one `useShallow`'d object of this unit: it is what
 * the memoised Chessboard element is built from, and it changes only when the
 * position, the lock or the browsed ply changes — never on a clock tick.
 *
 * M4: WHICH game is on the board comes from `selectBoardSource` first — a watch
 * (or a static replay) takes the same four board props from the watch slice
 * (`watchBoard.ts`), the orientation from the WATCHED seats, and a no-op move
 * callback, so a stray drop can never reach `actions.move`. Reading the source as
 * a STRING is what keeps a write to the watch slice from re-rendering this unit
 * while nothing is being watched.
 *
 * Two more things the board needs, both of them the old screen's `restrictPiece`
 * (index.js L4216-4234): the touch-piece rule's click reporter, and — while the
 * player is BROWSING an earlier ply of their own live game — the snap back to the
 * live position that the old `illegalTouchPieceCount` drove.
 *
 * FIX F-C: while self-analysis is `on` AND the action gate is actually settled
 * (review fix 2026-10-06: `mode` alone is not enough — see below), the board
 * instead shows the branch's tip and takes moves into `analysisMove` — see
 * `useAnalysis`'s own docblock for why that is inert while a live game is on
 * the board.
 *
 * REVIEW FIX (2026-10-06, F-C follow-up): the override used to apply
 * whenever `on` was true, full stop. Two gaps that opened: (1) leaving the
 * engine on from a finished watch/replay and then following a tournament
 * pairing onto a LIVE board (`watchTournamentGame` of a game still being
 * played) handed that live, spectated board a frozen branch-tip FEN and
 * `disabled: false` — the Rail's own reset only fires on `phase ===
 * 'starting'`, which a watch never raises; (2) fix F-D's assist mode
 * (`mode === 'assist'`, the live eval bar of an assisted game) is `on` too,
 * and must NOT take this board over — `actions.move` has to stay wired. Both
 * are the same fix: gate on `active = on && mode === 'analysis' &&
 * gateSettled`, reading the SAME `selectActionGate`/`settled` the Analysis
 * tile itself is disabled by (`rail/actions/gates.ts`) — "nothing unsettled
 * is on this board", not merely "the toggle happens to be on".
 */
import {useCallback} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useLiveGame} from '@/Store/liveGameStore';
import {
  boardPropsAtPly,
  orientationFor,
  selectBoardSource,
  selectPhase,
} from '@/Store/liveGame/selectors';

import {selectActionGate, settled} from '../../rail/actions/gates';
import {analysisMove} from '../../rail/analysis/analysisMove';
import {selectBranchTip, useAnalysis} from '../../rail/analysis/analysisStore';
import {analysisFenAtPly} from '../../rail/analysis/useEngine';
import {LAYOUT, useV2Ui} from '../../theme/useV2Ui';
import {watchBoardPropsAtPly} from './watchBoard';

export type BoardProps = ReturnType<typeof boardPropsAtPly>;

export type BoardFrameModel = {
  /**
   * M2: the analysis override below adds `movableColor` — `BoardProps` itself
   * (the core's `boardPropsAtPly` shape) is mirrored with the app and
   * untouchable, so the extra field is local to this type only.
   */
  board: BoardProps & {movableColor?: 'white' | 'black' | 'both'};
  orientation: 'white' | 'black';
  boardSize: number;
  /** the frame's padding left/right — narrower on the phone */
  padX: number;
  /**
   * The frame's padding above/below. 8 on the desktop since the clean-up of
   * 2026-10-05 (owner item 2): the board is height-bound, so the frame's top and
   * bottom margin was costing it 8 px a side for nothing.
   */
  padY: number;
  /** the store action the board drops a move into (a stable identity) */
  move: (from: string, to: string, promotion?: any) => any;
  /**
   * The touch-piece rule's click reporter — chessground's `select`, which fires
   * on a click AND on a drag-start. Every guard of the rule lives in
   * `actions.touchPiece`, so this may be called for any touch: chessground fires
   * `select` even on a DISABLED board (`board.js selectSquare` calls
   * `events.select` first).
   */
  restrictPiece: (piece: any, square: string) => void;
  /**
   * The player has browsed back in a LIVE game of their own. A finished game and
   * a watched game are browsed freely, so neither counts.
   */
  browsing: boolean;
  /** back to the live position (the tap-catcher's press) */
  snapToLive: () => void;
};

/** a spectator makes no moves; a stable identity so the View's memo holds. */
const noop = (): void => undefined;

/**
 * The browse-back snap (old `illegalTouchPieceCount` -> the notation's jump to
 * the last move, `NotationComponent` L518-524). ONE ui write and no
 * subscription, so it lives at module scope and its identity is stable for the
 * View's memo.
 */
const snapToLive = (): void => {
  useV2Ui.getState().setViewPly(null);
};

export function useBoardFrameModel(): BoardFrameModel {
  const {boardSize, flipped, viewPly, viewport} = useV2Ui(
    useShallow(s => ({
      boardSize: s.boardSize,
      flipped: s.flipped,
      viewPly: s.viewPly,
      viewport: s.viewport,
    })),
  );
  const source = useLiveGame(selectBoardSource);
  const watching = source === 'watch';
  const board = useLiveGame(
    useShallow(s =>
      watching ? watchBoardPropsAtPly(s, viewPly) : boardPropsAtPly(s, viewPly),
    ),
  );
  const myColor = useLiveGame(s => s.game.myColor);
  const phase = useLiveGame(selectPhase);
  const move = useLiveGame(s => s.actions.move);
  const touchPiece = useLiveGame(s => s.actions.touchPiece);
  const {mode, tipFen, branch} = useAnalysis(
    useShallow(s => ({
      mode: s.mode,
      tipFen: selectBranchTip(s),
      branch: s.branch,
    })),
  );
  // review fix 2026-10-06: "nothing unsettled is on this board" — the SAME
  // read the Analysis tile's own disabled state uses (`rail/actions/gates.ts`),
  // so a watch that moves from a finished game onto a live pairing closes this
  // override the instant it does, with no extra wiring in the watch reducer.
  const gateSettled = useLiveGame(s => settled(selectActionGate(s)));
  // fix F-D / D3: `mode === 'assist'` (the live eval bar of an assisted game)
  // must leave this board alone — `actions.move` stays wired, same as a game
  // with the engine off entirely.
  const active = mode === 'analysis' && gateSettled;

  /** touch_piece_rule: the board reports the square that was clicked. */
  const restrictPiece = useCallback(
    (piece: any, square: string) => {
      if (!piece) {
        return;
      }
      touchPiece({...piece, square});
    },
    [touchPiece],
  );

  // the branch's own root, one ply before `basePly` — needed only at cursor 0
  // (review fix 2026-10-06: `board.lastMoveFen` is null for ANY browsed ply,
  // `boardPropsAtPly`'s own contract, so it is the wrong fallback the moment
  // the branch did not start at the live tip). `null` while there is no
  // branch, or the branch starts at the very first ply (nothing before it) —
  // a selector that ignores its argument whenever that is so, which costs a
  // live game (branch always null) nothing.
  const branchBasePly = branch?.basePly ?? null;
  const branchRootFen = useLiveGame(s =>
    branchBasePly === null || branchBasePly === 0
      ? null
      : analysisFenAtPly(s, branchBasePly - 1),
  );

  // F-C: moves allowed from ANY browsed ply, onto the branch's tip — a plain
  // browse (no branch yet) shows `board.position` unchanged, `tipFen` is null
  const boardOnAnalysis = active
    ? {
        ...board,
        position: tipFen ?? board.position,
        disabled: false,
        arePremovesAllowed: false,
        // the highlight follows the branch, not the game: the fen BEFORE the
        // tip move (`Chessboard/index.web.tsx`'s `lastMoveFen` diffs the two)
        lastMoveFen: !branch
          ? board.lastMoveFen
          : branch.cursor === 0
          ? branchRootFen
          : branch.cursor === 1
          ? branch.baseFen
          : branch.moves[branch.cursor - 2].fen,
        restrictedPiece: null,
        // M2: BOTH colours' pieces are draggable during self-analysis — the
        // browsed ply (and every second branch move) is as often the OTHER
        // side's move as mine (`Chessboard`'s own `movableColor` prop).
        movableColor: 'both' as const,
      }
    : board;

  return {
    board: boardOnAnalysis,
    orientation: watching
      ? // white at the bottom unless the watcher IS the black seat (the replay
        // path), XOR'd with the user's flip
        orientationFor(
          board.boardOrientation === 'black' ? 'black' : 'white',
          flipped,
        )
      : orientationFor(myColor, flipped),
    boardSize,
    padX:
      viewport === 'phone'
        ? LAYOUT.boardFramePaddingPhone
        : LAYOUT.boardFramePadding,
    padY:
      viewport === 'phone'
        ? LAYOUT.boardFramePaddingPhone
        : LAYOUT.boardFrameVerticalPadding,
    move: active ? analysisMove : watching ? noop : move,
    restrictPiece: active || watching ? noop : restrictPiece,
    browsing: active
      ? false
      : viewPly !== null && !watching && phase === 'playing',
    snapToLive,
  };
}
